import { useState } from "react";
import { ArrowRight, Check } from "lucide-react";
import { MONTH_NAMES, daysInMonth, isValidDob, type Dob } from "../birthday";

/**
 * The first-run experience, rebuilt as its own component (extracted from App.tsx).
 * Four beats: a cinematic brand moment, the interests picker, an optional profile,
 * and a "your first round is ready" handoff. Every beat is skippable, works from
 * 320px phones to desktop, and respects reduced motion (all motion is CSS-driven).
 * House style: no em dashes or en dashes anywhere in copy.
 */

export interface OnboardOption {
  id: string;
  emoji: string;
  blurb: string;
}

export interface OnboardResult {
  mode: "guest" | "profile";
  picks: string[];
  firstName: string;
  lastName: string;
  email: string;
  dob: Dob | null;
}

type Beat = "brand" | "interests" | "profile" | "ready";

function haptic(ms: number) {
  if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
    try { navigator.vibrate(ms); } catch { /* not allowed */ }
  }
}

export function Onboarding({
  options,
  initialPicks = [],
  entry = "first-run",
  onComplete,
  onSkip,
}: {
  options: OnboardOption[];
  initialPicks?: string[];
  /** "profile" opens straight on the profile beat (the Birthday panel's re-entry). */
  entry?: "first-run" | "profile";
  onComplete: (r: OnboardResult) => void;
  onSkip: (r: OnboardResult) => void;
}) {
  const profileOnly = entry === "profile";
  const [beat, setBeat] = useState<Beat>(profileOnly ? "profile" : "brand");
  const [picks, setPicks] = useState<string[]>(initialPicks);
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [email, setEmail] = useState("");
  const [dobD, setDobD] = useState("");
  const [dobM, setDobM] = useState("");
  const [dobY, setDobY] = useState("");

  const dob = ((): Dob | null => {
    const candidate: Dob = { d: Number(dobD), m: Number(dobM), y: dobY ? Number(dobY) : undefined };
    return isValidDob(candidate) ? candidate : null;
  })();

  const result = (): OnboardResult => ({
    mode: profileOnly || first.trim() || email.trim() ? "profile" : "guest",
    picks,
    firstName: first.trim(),
    lastName: last.trim(),
    email: email.trim(),
    dob,
  });

  const dots: Beat[] = ["brand", "interests", "profile"];

  return (
    <div className="drawer-scrim center ob3-scrim" role="dialog" aria-modal="true" aria-label="Welcome to InSnip">
      <div className={`ob3-card beat-${beat}`} onClick={(e) => e.stopPropagation()}>
        {!profileOnly && beat !== "ready" ? (
          <>
            <button type="button" className="ob3-skip-corner" onClick={() => onSkip(result())}>
              Skip
            </button>
            <div className="ob3-dots" aria-hidden="true">
              {dots.map((b) => (
                <span key={b} className={beat === b ? "ob3-dot active" : "ob3-dot"} />
              ))}
            </div>
          </>
        ) : null}

        {beat === "brand" ? (
          <div className="ob3-beat ob3-brand">
            <span className="ob3-eyebrow">Welcome to</span>
            <div className="ob3-wordmark" aria-label="InSnip">
              <span className="ob3-wm-in">In</span>
              <span className="ob3-wm-snip">Snip</span>
            </div>
            <p className="ob3-motto">Pure signal, no noise.</p>
            <p className="ob3-value">
              The day's essential stories, distilled to what matters and backed by sources you
              can check.
            </p>
            <button
              type="button"
              className="ob3-cta"
              onClick={() => { haptic(10); setBeat("interests"); }}
            >
              Begin <ArrowRight size={16} aria-hidden="true" />
            </button>
          </div>
        ) : beat === "interests" ? (
          <div className="ob3-beat ob3-interests">
            <h2 className="ob3-title">What do you want to follow?</h2>
            <p className="ob3-sub">
              Pick a few. Your feed leads with what you love, and you can change it anytime.
            </p>
            <div className="ob3-grid">
              {options.map((opt) => {
                const picked = picks.includes(opt.id);
                return (
                  <button
                    key={opt.id}
                    type="button"
                    className={picked ? "ob3-chip picked" : "ob3-chip"}
                    aria-pressed={picked}
                    onClick={() => {
                      haptic(8);
                      setPicks((cur) =>
                        cur.includes(opt.id) ? cur.filter((c) => c !== opt.id) : [...cur, opt.id],
                      );
                    }}
                  >
                    <span className="ob3-emoji" aria-hidden="true">{opt.emoji}</span>
                    <strong>{opt.id}</strong>
                    <small>{opt.blurb}</small>
                    {picked ? (
                      <span className="ob3-check" aria-hidden="true"><Check size={12} /></span>
                    ) : null}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              className="ob3-cta"
              onClick={() => { haptic(10); setBeat("profile"); }}
            >
              {picks.length ? `Continue · ${picks.length} picked` : "Continue"}
            </button>
          </div>
        ) : beat === "profile" ? (
          <div className="ob3-beat ob3-profile">
            <h2 className="ob3-title">{profileOnly ? "A little about you" : "Make it truly yours"}</h2>
            <p className="ob3-sub">
              All optional and never shared. A name for your greeting, an email for your daily
              drop, a birthday for your day in history.
            </p>
            <div className="ob3-form">
              <div className="ob3-row">
                <input
                  className="ob3-input"
                  placeholder="First name"
                  value={first}
                  onChange={(e) => setFirst(e.target.value.slice(0, 40))}
                />
                <input
                  className="ob3-input"
                  placeholder="Last name"
                  value={last}
                  onChange={(e) => setLast(e.target.value.slice(0, 40))}
                />
              </div>
              <input
                className="ob3-input"
                type="email"
                inputMode="email"
                placeholder="Email (optional)"
                value={email}
                onChange={(e) => setEmail(e.target.value.slice(0, 160))}
              />
              <div className="ob3-dob">
                <select className="ob3-input" aria-label="Day of birth" value={dobD} onChange={(e) => setDobD(e.target.value)}>
                  <option value="">Day</option>
                  {Array.from(
                    { length: dobM ? daysInMonth(Number(dobM), dobY ? Number(dobY) : undefined) : 31 },
                    (_, i) => i + 1,
                  ).map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
                <select className="ob3-input" aria-label="Month of birth" value={dobM} onChange={(e) => setDobM(e.target.value)}>
                  <option value="">Month</option>
                  {MONTH_NAMES.map((m, i) => (
                    <option key={m} value={i + 1}>{m}</option>
                  ))}
                </select>
                <select className="ob3-input" aria-label="Year of birth (optional)" value={dobY} onChange={(e) => setDobY(e.target.value)}>
                  <option value="">Year</option>
                  {Array.from({ length: 100 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>
            <button
              type="button"
              className="ob3-cta"
              onClick={() => {
                haptic(10);
                if (profileOnly) onComplete(result());
                else setBeat("ready");
              }}
            >
              {profileOnly ? "Save" : "Finish"}
            </button>
            {!profileOnly ? (
              <button type="button" className="ob3-ghost" onClick={() => setBeat("ready")}>
                Skip this part
              </button>
            ) : null}
          </div>
        ) : (
          <div className="ob3-beat ob3-ready">
            {/* A ring that spins forever is the universal sign for "still working on it", and
                it sat directly above the words "Your first round is ready". It now draws once
                and rests, complete, with the number of stories waiting inside it. The count is
                a SIBLING of the ring, not a child: the ring is masked hollow, and a mask
                applies to an element's children too, so a count inside it was invisible. */}
            <div className="ob3-mark" aria-hidden="true">
              <span className="ob3-ring" />
              <span className="ob3-ring-count">10</span>
            </div>
            {first ? (
              <p className="ob3-hello">Welcome aboard, <strong>{first}</strong></p>
            ) : null}
            <h2 className="ob3-title">Your first round is ready</h2>
            <p className="ob3-sub">Ten stories, a few minutes, zero noise.</p>
            <button type="button" className="ob3-cta" onClick={() => { haptic(12); onComplete(result()); }}>
              Start reading <ArrowRight size={16} aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
